import React, { useEffect, useState } from 'react'

import { IconButton, Modal, Paper, Slide, Tab, Tabs } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import LocalOfferIcon from '@material-ui/icons/LocalOffer'

import { Container } from '../../components'
import { Concession } from '../../models/Concession'
import { ConcessionForm } from './ConcessionForm'
import { ConcessionMessagesTab } from './ConcessionMessagesTab'
import { useConcessionStyles } from './styles'

interface Props {
    open: boolean
    concession?: Concession
    handleClose: () => void
    onCreated?: (concession: Concession) => void
    defaultActive?: boolean
}

export const ConcessionModal: React.FC<Props> = ({
    open,
    concession,
    handleClose,
    onCreated,
    defaultActive,
}) => {
    const classes = useConcessionStyles()
    const [tab, setTab] = useState(0)

    // Reset to the Details tab whenever the modal opens or the record changes.
    useEffect(() => {
        if (open) setTab(0)
    }, [open, concession?.id])

    const isEditing = !!concession

    return (
        <Modal
            open={open}
            onClose={handleClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper className={classes.paper}>
                    <Container
                        direction="column"
                        style={{
                            width: 'min(960px, calc(100vw - 80px))',
                            height: 'calc(100vh - 120px)',
                            maxHeight: 820,
                            overflow: 'hidden',
                        }}
                    >
                        {/* Header */}
                        <div className={classes.header}>
                            <div className={classes.headerIcon}>
                                <LocalOfferIcon />
                            </div>
                            <Container direction="column" flex={1}>
                                <span className={classes.headerTitle}>
                                    {isEditing
                                        ? concession?.name || 'Edit Concession'
                                        : 'New Concession'}
                                </span>
                                <span className={classes.headerSubtitle}>
                                    {isEditing
                                        ? 'Edit this renewal incentive'
                                        : 'Create a renewal incentive for tenants'}
                                </span>
                            </Container>
                            <IconButton size="small" onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </div>

                        {/* Tabs (Messages require a saved concession) */}
                        {isEditing && (
                            <Tabs
                                value={tab}
                                onChange={(_, value) => setTab(value)}
                                indicatorColor="primary"
                                textColor="primary"
                                className={classes.tabs}
                            >
                                <Tab label="Details" />
                                <Tab label="Messages" />
                            </Tabs>
                        )}

                        {/* Body */}
                        {open && (!isEditing || tab === 0) && (
                            <ConcessionForm
                                key={concession?.id ?? 'new'}
                                concession={concession}
                                handleClose={handleClose}
                                onCreated={onCreated}
                                defaultActive={defaultActive}
                            />
                        )}
                        {open && isEditing && tab === 1 && concession && (
                            <ConcessionMessagesTab
                                concessionId={concession.id}
                                lifetimeStart={concession.start_date}
                                lifetimeEnd={concession.end_date}
                            />
                        )}
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
